import { createContext, useContext } from "react";
import type { ReactNode } from "react";
import type { CurrentPlan } from "@circle-react/types";
import { useCurrentPlanApi } from "../../hooks/useCurrentPlanApi";

interface BillingContextValue {
  currentPlan?: CurrentPlan;
  errorFetchingCurrentPlan: unknown;
  isCurrentPlanLoading: boolean;
  refetchCurrentPlan: () => unknown;
  showAddons?: boolean;
  showBillingDetails?: boolean;
  showBillingPortal?: boolean;
}

export const BillingContext = createContext<BillingContextValue>({
  currentPlan: undefined,
  showAddons: false,
  showBillingDetails: false,
  showBillingPortal: false,
  isCurrentPlanLoading: false,
  errorFetchingCurrentPlan: null,
  refetchCurrentPlan: () => {},
});

BillingContext.displayName = "BillingContext";

interface BillingProviderProps {
  children: ReactNode;
  forceFromSubscription?: boolean;
  showAddons?: boolean;
  showBillingDetails?: boolean;
  showBillingPortal?: boolean;
}

export const BillingProvider = ({
  forceFromSubscription = true,
  showAddons,
  showBillingDetails,
  showBillingPortal,
  children,
}: BillingProviderProps) => {
  const {
    currentPlan,
    isCurrentPlanLoading,
    errorFetchingCurrentPlan,
    refetchCurrentPlan,
  } = useCurrentPlanApi(forceFromSubscription);

  return (
    <BillingContext.Provider
      value={{
        currentPlan,
        showAddons,
        showBillingDetails,
        showBillingPortal,
        isCurrentPlanLoading,
        errorFetchingCurrentPlan,
        refetchCurrentPlan,
      }}
    >
      {children}
    </BillingContext.Provider>
  );
};

export const useBillingContext = () => useContext(BillingContext);
